import { z } from 'zod';
import { BundleStatus, BusinessType, Country, Language, NumberType, PlanTier, ProvisioningStatus, SubscriptionStatus, } from '../enums.js';
import { E164, LocalDate, Timezone } from '../primitives.js';
import { MAX_TRANSCRIPT_TURNS, TranscriptTurnSchema } from '../schemas/call.js';
import { ServiceAnswerInputsSchema } from '../schemas/serviceQuestions.js';
import { CustomerNameSchema } from '../schemas/customerName.js';
/**
 * Callable Cloud Function request/response contracts (ADR-0008 rule 2): the
 * same types are used by the typed FE adapter wrappers (ADR-0003) and the
 * function handlers, so a signature change breaks compilation on both sides.
 */
// createBusiness — create an additional named business/location under the
// caller's account (doc 07 §5.1). Server-side so the account quota is enforced
// and business-doc + availability + copied services are written atomically.
export const CreateBusinessRequest = z.object({
    name: z.string().min(1).max(120),
    /** What kind of business this is (doc 23). Optional on the wire so an older
     * client keeps working; the field is write-once, so creation is the only
     * place it can be set without also being the place it can be changed. */
    businessType: BusinessType.optional(),
    country: Country,
    language: Language,
    timezone: Timezone,
    greeting: z.string().max(300).optional(),
    /** Duplicate services/hours/greeting from a business the caller owns. */
    copyFromBusinessId: z.string().min(1).optional(),
});
export const CreateBusinessResponse = z.object({
    businessId: z.string(),
});
/**
 * setBusinessRegion — change a business's country, and with it the scheduling
 * timezone (doc 13a D2).
 *
 * Server-side because the two must move together and neither is safe to take
 * from the client: `timezone` is load-bearing for every slot computation and
 * booking read-back, and an invalid IANA string written straight to Firestore
 * takes the business's phone line down (the gateway parses it on every inbound
 * call). The country is also frozen once a number is held — the number is
 * national, so its country IS the business's country.
 */
export const SetBusinessRegionRequest = z.object({
    businessId: z.string().min(1),
    country: Country,
});
export const SetBusinessRegionResponse = z.object({
    country: Country,
    /** Derived server-side from the country; never accepted from the caller. */
    timezone: z.string(),
});
// archiveBusiness — retire a business (doc 07 §4.3). All numbers must be released
// first; history is kept read-only. Idempotent (re-archiving is a no-op).
export const ArchiveBusinessRequest = z.object({
    businessId: z.string().min(1),
});
export const ArchiveBusinessResponse = z.object({
    status: z.literal('archived'),
});
/**
 * Why a forward-target number could not be bought for a business (doc 13a D2).
 *
 * These used to be swallowed by a GB→US fallback cascade. They are surfaced
 * individually now because they are three different conversations: `no_inventory`
 * may resolve by retrying later, `regulatory` is the KYC gate (and the thing the
 * Telnyx work in doc 17 is meant to unblock), and `type_unavailable` is an
 * account-level capability we have to ask the provider to enable.
 */
export const ForwardNumberFailure = z.enum([
    /** The provider has no matching numbers in stock right now. */
    'no_inventory',
    /** Purchase refused without an approved regulatory bundle for this country. */
    'regulatory',
    /** This number type isn't enabled for this country on our provider account. */
    'type_unavailable',
    /** Anything else — network, auth, an unrecognised provider error. */
    'unknown',
    /**
     * We do not serve this country, or this business, on a number of its own —
     * and numbers are no longer bought for anyone (doc 18 D7). Every business is
     * served through a shared number identified by its diverted-from, so a
     * country with no shared number has nothing to offer. Distinct from
     * `no_inventory` because the follow-up differs: that one is "try again
     * later", this one is "not yet".
     */
    'not_served',
]);
/**
 * Number types tried when buying a forward-target number, in order. BOTH are
 * domestic, which is the point: the owner diverts their own line to this number,
 * and a divert is billed as an outbound call from their line. Anything outside
 * their country is an international call on every single inbound call, for its
 * full duration.
 *
 * Toll-free is deliberately absent: it is national but routinely excluded from
 * the flat-rate minute bundles the forwarding-cost guidance assumes.
 */
export const FORWARD_NUMBER_TYPES = ['local', 'mobile'];
// activateBusiness — turn a draft business live by auto-buying a cheap
// forward-target number (doc 13a). No KYC/bundle: the number is bought on the
// parent account and the owner forwards their real line to it. Idempotent —
// re-activating an already-active business returns its existing number.
export const ActivateBusinessRequest = z.object({
    businessId: z.string().min(1),
});
export const ActivateBusinessResponse = z.object({
    status: z.literal('active'),
    phoneNumber: z.string(),
    /** ISO 3166-1 alpha-2 the number was bought in. Always the business's own
     * country — see `FORWARD_NUMBER_TYPES` for why a foreign number is not an
     * acceptable fallback. Kept as a field because it is the fact the forwarding
     * instructions and cost guidance depend on. */
    isoCountry: z.string(),
});
/**
 * `failed-precondition` detail returned by `activateBusiness` when no national
 * number can be bought. The portal turns this into a "contact support" state
 * rather than a retry button — none of these clear by trying again immediately.
 */
export const ActivateBusinessErrorDetail = z.object({
    reason: ForwardNumberFailure,
    /** The country we tried, for the support message. */
    country: Country,
});
// beginForwardingTest — open a short window during which the next inbound call
// on the business's forward number is treated as the owner's "confirm the divert
// landed" test (doc 13a): the gateway marks the number verified and answers with
// a confirmation instead of running the agent.
export const BeginForwardingTestRequest = z.object({
    businessId: z.string().min(1),
});
// declareSourceNumber — the owner tells us which line they will divert (doc 18).
// Mandatory: without it there is nothing for the verification call to be checked
// against, and an unchecked ForwardedFrom cannot be allowed to grant a route.
export const DeclareSourceNumberRequest = z.object({
    businessId: z.string().min(1),
    /** The owner's own business line, as their customers dial it. Accepted in any
     * shape the owner is likely to type; the server normalises and echoes back
     * the canonical E.164 it stored. */
    sourceNumber: z.string().min(4).max(24),
});
export const DeclareSourceNumberResponse = z.object({
    /** Canonical E.164 of what we stored — the portal should display THIS, not
     * what the owner typed, so any normalisation is visible to them immediately. */
    sourceNumber: z.string(),
    /** True when this REPLACED a previously declared line rather than being the
     * first one. Any routing grant and verified state for the old number are
     * dropped, so the owner has to prove the divert on the new line before it can
     * receive calls — the portal says so rather than leaving a green check that
     * refers to a number no longer on the doc. */
    reverificationRequired: z.boolean(),
});
// completeOnboarding — finish setup. Server-side because it enforces the rule
// that a business cannot be "set up" while it is unable to answer a call
// (doc 18 §7.3); as a client write, that rule was only a UI convention.
/**
 * Start Stripe Checkout for one business (doc 23 — paid from day one).
 *
 * The server picks the Stripe price from the plan and the business's currency;
 * the client sends the TIER, never a price id. A client-supplied price id is a
 * client-supplied price — anyone could subscribe at a price object of their
 * choosing, including one they created in their own test mode.
 */
export const CreateCheckoutSessionRequest = z.object({
    businessId: z.string().min(1),
    /** Required for a `legacy` business. Ignored for `prepaid_v1`, whose only
     * subscription is the platform fee (doc 42 §3.1) — the server decides which
     * from the business's billing mode, never from the request. */
    // `nullish`: Firebase's callable SDK sends an undefined property as null.
    plan: PlanTier.nullish().transform((v) => v ?? undefined),
    /** Prepaid only: one minute package billed TOGETHER with the first
     *  subscription payment (a one-time line on the first invoice). Credited
     *  only from that invoice once it is verified paid, never from this field. */
    packageId: z.string().min(1).max(64).nullish().transform((v) => v ?? undefined),
    /** Where Checkout returns to. Validated server-side against our own origin —
     * an open redirect on a payment flow is a phishing primitive. */
    returnOrigin: z.string().url(),
});
export const CreateCheckoutSessionResponse = z.object({
    /** Stripe-hosted Checkout URL to send the browser to. */
    url: z.string().url(),
});
/** Open the Stripe billing portal — card changes, invoices, cancellation.
 *  We deliberately implement none of those ourselves. */
export const CreateBillingPortalSessionRequest = z.object({
    businessId: z.string().min(1),
    returnOrigin: z.string().url(),
});
export const CreateBillingPortalSessionResponse = z.object({ url: z.string().url() });
/**
 * Poll the subscription after Checkout returns (doc 23).
 *
 * Checkout's redirect and Stripe's webhook race, and the redirect usually wins:
 * the owner lands on the success screen a beat before `checkout.session.completed`
 * has been applied. Rather than show them "not subscribed" and hope, the success
 * screen calls this, which reads the subscription STRAIGHT FROM STRIPE and
 * writes it — so the flow is correct even if the webhook is late, lost, or was
 * never configured. The webhook remains the source of truth for everything
 * afterwards (renewals, failures, cancellations), which no redirect can observe.
 */
export const SyncSubscriptionRequest = z.object({
    businessId: z.string().min(1),
    /**
     * The `session_id` Stripe puts in the success URL, when we have it.
     *
     * Not a shortcut and not a trust boundary — ownership is still proved by the
     * business, never by this. It is here because the fallback lookup goes
     * through `subscriptions.search`, and Stripe's search index is EVENTUALLY
     * CONSISTENT with a lag of up to a minute. A subscription created four
     * seconds ago is exactly the case the index does not have yet, and that is
     * the only case this callable exists to serve — so without the session id the
     * happy path silently degrades to "your subscription is still being
     * confirmed" for every single customer.
     *
     * Retrieving the session is a direct read and has no such lag. Optional
     * because the settings screen can also sync with no session in hand.
     */
    sessionId: z.string().min(1).nullish().transform((v) => v ?? undefined),
});
export const SyncSubscriptionResponse = z.object({
    status: SubscriptionStatus,
    plan: PlanTier.nullable(),
});
export const CompleteOnboardingRequest = z.object({
    businessId: z.string().min(1),
});
export const CompleteOnboardingResponse = z.object({
    onboardingCompletedAt: z.date(),
});
// cancelForwardingTest — close an open verification window early, so the owner
// can go back and correct the line the check is run against (doc 18 D2).
export const CancelForwardingTestRequest = z.object({
    businessId: z.string().min(1),
});
export const CancelForwardingTestResponse = z.object({
    /** False when there was no window open — already the state the caller wanted. */
    cancelled: z.boolean(),
});
export const BeginForwardingTestResponse = z.object({
    /** The number the owner should trigger a forwarded call to (their own line → this). */
    phoneNumber: z.string(),
    /** How long the verification window stays open, in seconds. */
    expiresInSec: z.number().int(),
});
/** How long a forwarding-test window stays open (doc 13a). */
export const FORWARDING_TEST_WINDOW_SEC = 300;
// getComplianceRequirements — country/type-specific KYC checklist for the UI (doc 05 §2.5).
export const GetComplianceRequirementsRequest = z.object({
    country: Country,
    numberType: NumberType.optional(),
});
export const DocumentRequirementSchema = z.object({
    /** Machine `Type` sent to Twilio's Supporting Documents API. */
    twilioType: z.string(),
    label: z.string(),
    description: z.string(),
});
export const ComplianceRequirementsSchema = z.object({
    country: Country,
    numberType: NumberType,
    /** e.g. "NIP / KRS" (PL), "SIREN / SIRET" (FR), "HRB / Tax ID" (DE). */
    idLabel: z.string(),
    addressRequirement: z.enum(['in_country_locality', 'in_country', 'anywhere']),
    requiresCity: z.boolean(),
    documents: z.array(DocumentRequirementSchema),
    /** EndUser attribute keys collected during onboarding. */
    endUserAttributes: z.array(z.string()),
});
// checkNumberAvailability — is this (country, numberType) purchasable on our
// Twilio account yet? Surfaces the geo/regulatory enablement gap as a clear
// in-product message instead of a raw 404 at purchase time (doc 05 live-validation).
export const CheckNumberAvailabilityRequest = z.object({
    country: Country,
    numberType: NumberType.optional(),
});
export const NumberAvailabilitySchema = z.object({
    country: Country,
    numberType: NumberType,
    available: z.boolean(),
    /** `ok` = buyable; `not_offered` = type not enabled for this account (404);
     *  `no_inventory` = enabled but temporarily out of stock. */
    reason: z.enum(['ok', 'not_offered', 'no_inventory']),
    /** Twilio number types currently offered for the country, e.g. ['local','mobile']. */
    offeredTypes: z.array(z.string()),
});
// submitCompliance — kicks off the KYC bundle flow for one (country, numberType) (doc 05 §2.6).
export const SubmitComplianceRequest = z.object({
    businessId: z.string().min(1),
    /** Defaults to the business's own country when omitted. */
    country: Country.optional(),
    /** Defaults to the country's default number type. */
    numberType: NumberType.optional(),
});
export const SubmitComplianceResponse = z.object({
    status: z.literal('pending_review'),
    bundleSid: z.string(),
    bundleKey: z.string(),
});
// syncComplianceStatus — poll fallback that reconciles the local bundle status
// with Twilio's live status (doc 05 §4.6). Twilio's bundle `statusCallback` is
// best-effort, so the client calls this on page load to self-heal a missed
// approval callback instead of being stuck at pending_review forever.
export const SyncComplianceStatusRequest = z.object({
    businessId: z.string().min(1),
    /** Defaults to the primary bundle (business country + its default number type). */
    bundleKey: z.string().optional(),
});
export const SyncComplianceStatusResponse = z.object({
    businessStatus: ProvisioningStatus,
    bundleStatus: BundleStatus,
});
// provisionNumber — search + purchase a number backed by an approved bundle (doc 05 §3).
export const ProvisionNumberRequest = z.object({
    businessId: z.string().min(1),
    /** Defaults to the business country; a different country requires its own approved bundle. */
    country: Country.optional(),
    numberType: NumberType.optional(),
    /** Required for local numbers (DE/PL) — locality must match the address. */
    city: z.string().optional(),
    label: z.string().max(60).optional(),
    /** Idempotency key (also the `numbers` subcollection doc id). Generated client-side. */
    requestId: z.string().min(1).max(120).optional(),
});
export const ProvisionNumberResponse = z.object({
    status: z.literal('active'),
    phoneNumber: z.string(),
    numberId: z.string(),
});
// releaseNumber — release one number; the bundle is retained for reuse (doc 05 §3).
export const ReleaseNumberRequest = z.object({
    businessId: z.string().min(1),
    /** The E.164 to release; defaults to the business's primary number. */
    phoneNumber: z.string().optional(),
});
export const ReleaseNumberResponse = z.object({
    status: z.literal('released'),
    /** Remaining active number count for the business. */
    remaining: z.number().int(),
});
/** Callable function names (must match the exported symbol names in `api/`). */
// ── Owner booking from the dashboard (doc 43) ─────────────────────────────────
// Times travel as the BUSINESS's local date + "HH:mm" and are converted on the
// server in the business timezone (D5) — the browser's own zone never matters.
/** "HH:mm", 00:00–23:59. */
export const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/** Longest booking the owner may enter (doc 43 D6): the whole day. It was the
 *  8 h service cap until all-day services (doc 45), whose bookings run from
 *  opening to closing and can be longer; same-day is still enforced. */
export const MAX_OWNER_BOOKING_MIN = 24 * 60;
/** An overlap is always refused (doc 85): the `allowOverlap` override an
 *  older form still sends is stripped by the schema, never honoured. */
const BookingTime = {
    date: LocalDate,
    startTime: LocalTime,
    endTime: LocalTime,
};
export const CreateManualBookingRequest = z.object({
    businessId: z.string().min(1),
    serviceId: z.string().min(1),
    /** null = any available staff member (D7); ignored for an unstaffed business. */
    staffId: z.string().min(1).nullable().default(null),
    customerPhone: E164,
    /** The same name rule as the assistant's bookings (doc 65). */
    customerName: CustomerNameSchema,
    note: z.string().trim().max(300).default(''),
    /**
     * Answers to the service's questions (doc 64), checked on the server against
     * the service as it is INSIDE the booking transaction. Nullish because the
     * callable SDK sends an omitted field as null; an older form sends none and
     * is refused for a service with questions, never booked without them.
     */
    serviceAnswers: ServiceAnswerInputsSchema.nullish().transform((v) => v ?? []),
    ...BookingTime,
});
export const RescheduleBookingRequest = z.object({
    businessId: z.string().min(1),
    appointmentId: z.string().min(1),
    /**
     * Who holds the moved booking (doc 85): a member's id, or null for any
     * available one. ABSENT — a form from before doc 85 — keeps the booking's
     * own member. Always send it as a value: the callable SDK turns an explicit
     * `undefined` into null, which means "any".
     */
    staffId: z.string().min(1).nullable().optional(),
    ...BookingTime,
});
export const OwnerBookingResponse = z.object({
    appointmentId: z.string(),
    staffId: z.string().nullable(),
    staffName: z.string().nullable(),
});
/** `details.reason` of a `failed-precondition` from the two callables above. */
export const OwnerBookingRefusal = z.enum([
    'slot_taken',
    'in_past',
    'cancelled',
    'invalid_time',
    'no_staff',
    /** The business has staff and nobody who could take it works over the
     * whole time — the chosen member, or every eligible one for "any" (doc 85). */
    'staff_unavailable',
    /** A question of the service has no answer (doc 64) — `details.missingQuestionIds`. */
    'service_answers_required',
    /** An answer is for a question the service no longer asks (it was edited
     * meanwhile), repeated, or too long — `details.invalidQuestionIds`. */
    'invalid_service_answers',
]);
/** The `details` of an owner-booking refusal. */
export const OwnerBookingRefusalDetails = z.object({
    reason: OwnerBookingRefusal,
    missingQuestionIds: z.array(z.string()).optional(),
    invalidQuestionIds: z.array(z.string()).optional(),
});
export const CALLABLE_NAMES = {
    createBusiness: 'createBusiness',
    activateBusiness: 'activateBusiness',
    beginForwardingTest: 'beginForwardingTest',
    cancelForwardingTest: 'cancelForwardingTest',
    declareSourceNumber: 'declareSourceNumber',
    completeOnboarding: 'completeOnboarding',
    setBusinessRegion: 'setBusinessRegion',
    archiveBusiness: 'archiveBusiness',
    getComplianceRequirements: 'getComplianceRequirements',
    checkNumberAvailability: 'checkNumberAvailability',
    submitCompliance: 'submitCompliance',
    syncComplianceStatus: 'syncComplianceStatus',
    provisionNumber: 'provisionNumber',
    releaseNumber: 'releaseNumber',
    createCheckoutSession: 'createCheckoutSession',
    createBillingPortalSession: 'createBillingPortalSession',
    syncSubscription: 'syncSubscription',
    // Prepaid billing (doc 42 §13).
    getBillingOverview: 'getBillingOverview',
    checkIncludedMinutes: 'checkIncludedMinutes',
    prepareActivation: 'prepareActivation',
    quoteMinuteTopup: 'quoteMinuteTopup',
    createMinuteTopup: 'createMinuteTopup',
    syncMinuteTopup: 'syncMinuteTopup',
    cancelMinuteTopup: 'cancelMinuteTopup',
    createBillingPaymentSetup: 'createBillingPaymentSetup',
    completeBillingPaymentSetup: 'completeBillingPaymentSetup',
    saveAutoTopupSettings: 'saveAutoTopupSettings',
    saveBillingSmsSettings: 'saveBillingSmsSettings',
    listBillingHistory: 'listBillingHistory',
    // Owner booking from the dashboard (doc 43).
    createManualBooking: 'createManualBooking',
    rescheduleBooking: 'rescheduleBooking',
    // Account emails (doc 88).
    sendVerificationEmail: 'sendVerificationEmail',
    requestPasswordReset: 'requestPasswordReset',
};
/**
 * Metadata used to derive number-type / inventory-search / provider defaults per
 * country (doc 05 §2.7; provider split investigated in doc 17).
 *
 * `provider` is a per-country BUSINESS DECISION (which vendor's inventory +
 * regulatory flow we buy that country's numbers from), not a live capability
 * check — it does not by itself make Telnyx numbers purchasable; that needs a
 * Telnyx provisioning/compliance service and voice-gateway adapter analogous to
 * the existing Twilio ones (doc 17 §Deferred). Until those exist, only `twilio`
 * is a working provider end-to-end.
 *
 * PL defaults to `telnyx` because Twilio's PL bundle KYC has repeatedly stalled
 * in prod (see `kyc-stuck-pending-no-reconcile` memory) and the account already
 * has PL inventory on Telnyx. FR/DE stay on `twilio`, the only provider with a
 * working purchase + compliance implementation today.
 */
export const COUNTRY_DEFAULTS = {
    // FR: `local` (in-locality address). Chosen over national `09` because national
    // is not enabled/available on our Twilio account (doc 05 live-validation); local
    // returns live inventory today. Revisit if/when national is enabled.
    FR: { numberType: 'local', requiresCity: true, provider: 'twilio', timezone: 'Europe/Paris', language: 'fr', currency: 'EUR' },
    DE: { numberType: 'local', requiresCity: true, provider: 'twilio', timezone: 'Europe/Berlin', language: 'de', currency: 'EUR' },
    // PL is `mobile`, not local, and that is a consequence of how we buy it: the
    // account holds ONE approved Telnyx requirement group for Poland, and it is
    // scoped to (PL, mobile, ordering). Ordering against it is what makes a Polish
    // number answer calls the same day instead of sitting in
    // `requirement-info-pending` for days (doc 17 — a PL *local* order came back
    // pending with five outstanding requirements). The group id lives server-side
    // in `api/src/telnyx/requirementGroups.ts`; this field must keep agreeing with
    // its `numberType`, and a test asserts that. Mobile also drops the locality
    // requirement — the address requirement for mobile is "anywhere".
    PL: { numberType: 'mobile', requiresCity: false, provider: 'telnyx', timezone: 'Europe/Warsaw', language: 'pl', currency: 'PLN' },
};
/** Human name of a country, for anywhere we put one in front of an owner. */
export const COUNTRY_LABELS = {
    FR: 'France',
    DE: 'Germany',
    PL: 'Poland',
};
/**
 * Countries a business may be created in — or moved to — **today**.
 *
 * Deliberately narrower than the `Country` enum, and that gap is the point.
 * `Country` is the set of countries the system can *represent*: businesses
 * created before this list existed are FR/DE, their docs must keep parsing, and
 * `COUNTRY_DEFAULTS` / `complianceRequirements` still have to answer for them.
 * This list is the set we are willing to *sell* right now, which is only PL —
 * it is the one country with a working end-to-end number purchase (an approved
 * Telnyx requirement group makes a Polish mobile answer calls the same day,
 * doc 17 §6d), while FR/DE go through Twilio's bundle KYC, which has stalled in
 * `pending_review` in production with no reliable callback.
 *
 * Selling a country whose numbers we cannot actually deliver is worse than not
 * offering it: the owner finishes setup and gets a business that never rings.
 * Re-adding a country here is the single edit that re-opens it.
 */
export const SELECTABLE_COUNTRIES = ['PL'];
/** Pre-selected in every country picker. Always a member of {@link SELECTABLE_COUNTRIES}. */
export const DEFAULT_COUNTRY = SELECTABLE_COUNTRIES[0];
/** Is this a country we currently offer? Enforced server-side, not just in the UI. */
export function isSelectableCountry(country) {
    return SELECTABLE_COUNTRIES.includes(country);
}
/**
 * The options a country picker should show for a business currently in
 * `current`. Normally just {@link SELECTABLE_COUNTRIES} — but a legacy FR/DE
 * business has to be able to render its own country, or the picker would
 * silently display Poland for a business that is not in Poland.
 */
export function countryOptions(current) {
    const options = [...SELECTABLE_COUNTRIES];
    if (current && !options.includes(current))
        options.unshift(current);
    return options;
}
/**
 * KYC requirements per (country, numberType), driving the onboarding UI and the
 * EndUser/SupportingDocument construction.
 *
 * VALIDATED against the LIVE Twilio Regulations catalog (doc 05 Phase 0.1,
 * `dump:regulations`): the business end-user requires `business_name` +
 * `first_name` + `last_name`; the required document is a `business_registration`
 * excerpt (KRS/NIP/REGON in PL) carrying `business_name` + `document_number`,
 * with an optional `power_of_attorney` authorizing the representative. There is
 * NO proof-of-address / utility-bill document requirement — the registered
 * address is captured by the Address resource, not a document.
 */
export function complianceRequirements(country, numberType = COUNTRY_DEFAULTS[country].numberType) {
    const businessRegistration = {
        twilioType: 'business_registration',
        label: country === 'FR'
            ? 'K-bis excerpt (business registration)'
            : country === 'PL'
                ? 'KRS / NIP / REGON excerpt (business register)'
                : 'Handelsregisterauszug / Gewerbeanmeldung',
        description: 'An official excerpt from the business register showing the legal name and registration number.',
    };
    const powerOfAttorney = {
        twilioType: 'power_of_attorney',
        label: 'Authorization of representative (optional)',
        description: 'Power of attorney or similar — only if the representative is not named in the register excerpt.',
    };
    // Mobile / toll-free relax the address to "anywhere"; local/national need in-country.
    const addressRequirement = numberType === 'mobile' || numberType === 'toll-free'
        ? 'anywhere'
        : numberType === 'local'
            ? 'in_country_locality'
            : 'in_country';
    return {
        country,
        numberType,
        idLabel: country === 'FR' ? 'SIREN / SIRET' : country === 'PL' ? 'NIP / KRS' : 'HRB / Tax ID',
        addressRequirement,
        requiresCity: addressRequirement === 'in_country_locality',
        documents: [businessRegistration, powerOfAttorney],
        endUserAttributes: ['business_name', 'first_name', 'last_name'],
    };
}
// ── Demo calls (doc 13a §6a) ─────────────────────────────────────────────────
/** What the browser posts when a setup demo call ends. */
export const FinalizeDemoCallRequest = z.object({
    businessId: z.string().min(1),
    /** The demo session the gateway created, so it can end the provider call
     *  (doc 33 W10 #5). Optional for clients older than that. */
    sessionId: z.string().max(64).optional(),
    startedAt: z.number().int().nonnegative(),
    endedBy: z.enum(['user', 'timeout', 'error']).default('user'),
    transcript: z.array(TranscriptTurnSchema).max(MAX_TRANSCRIPT_TURNS).default([]),
});
//# sourceMappingURL=index.js.map